import { Entity } from '@api';
import { Drawer } from '@design/components/drawer/Drawer';
import { DrawerContentWrapper } from '@terminal/components/drawer-content-wrapper/DrawerContentWrapper';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useThemeStore } from '@terminal/components/theme-controller/stores/useThemeStore';
import { isDarkTheme } from '@terminal/components/theme-controller/utils/theme-utils/theme-utils';
import { useBrandsAndProducts } from '@terminal/react-query-hooks/knowledge-graph/useBrandsAndProducts';
import { useBrandAndProductDrawer } from '@terminal/widgets/knowledge-graph-widget/components/brands-and-products-tab/useBrandAndProductDrawer';
import React from 'react';

import styles from './BrandAndProductDrawer.module.scss';
import { BrandAndProductDrawerContent } from './components/BrandAndProductDrawerContent';

export interface BrandAndProductDrawerProps {
  entity: Entity;
}

export const BrandAndProductDrawer = ({
  entity,
}: BrandAndProductDrawerProps) => {
  const { closeDrawer, isDrawerOpen, product } = useBrandAndProductDrawer();
  const { data, error } = useBrandsAndProducts(
    entity.tag,
    isDrawerOpen && !!product
  );
  const themeName = useThemeStore(state => state.themeName);

  if (error && isDrawerOpen) {
    throw error;
  }

  const productAndBrand = data?.find(item => item.product === product);
  const isDark = isDarkTheme(themeName);
  const logoUrl = isDark
    ? entity.logo_urls.light_png
    : entity.logo_urls.dark_png;

  if (!productAndBrand) {
    return null;
  }

  return (
    <Drawer
      data-testid="brand-product-exposure-drawer"
      isOpen={isDrawerOpen}
      handleCloseClick={closeDrawer}
      headerButton={
        <div className={styles.headerTitle}>{productAndBrand.product}</div>
      }
    >
      <DrawerContentWrapper>
        <ErrorBoundaryWrapper>
          <BrandAndProductDrawerContent
            entity={entity}
            logoUrl={logoUrl}
            productAndBrand={productAndBrand}
          />
        </ErrorBoundaryWrapper>
      </DrawerContentWrapper>
    </Drawer>
  );
};
